import { useRef, useEffect, useCallback } from 'react';
import * as echarts from 'echarts';

/**
 * Custom hook for managing ECharts instance
 */
export const useEChart = (options, dependencies = []) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  /**
   * Resize chart handler
   */
  const handleResize = useCallback(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.resize();
    }
  }, []);

  /**
   * Initialize or update chart
   */
  useEffect(() => {
    if (!chartRef.current || !options || Object.keys(options).length === 0) {
      return;
    }

    // Initialize chart if not exists
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current, null, {
        renderer: 'canvas',
        useDirtyRect: false // Disable dirty rect optimization for better performance in this use case
      });
    }

    // Update chart options
    chartInstanceRef.current.setOption(options, true);

    // Add resize listener
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [...dependencies, options, handleResize]);

  /**
   * Cleanup chart instance
   */
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  /**
   * Manual resize function
   */
  const resize = useCallback(() => {
    handleResize();
  }, [handleResize]);

  /**
   * Get chart instance (for advanced usage)
   */
  const getChartInstance = useCallback(() => {
    return chartInstanceRef.current;
  }, []);

  /**
   * Show loading state
   */
  const showLoading = useCallback((text = 'Loading...') => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.showLoading('default', {
        text,
        color: '#1f77b4',
        textColor: '#000',
        maskColor: 'rgba(255, 255, 255, 0.8)',
        zlevel: 0
      });
    }
  }, []);

  /**
   * Hide loading state
   */
  const hideLoading = useCallback(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.hideLoading();
    }
  }, []);

  return {
    chartRef,
    resize,
    getChartInstance,
    showLoading,
    hideLoading
  };
};
